import type { FC } from 'react';
import { useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Collapse,
  Container,
  Typography,
  Divider,
  Button
} from '@mui/material';

interface FaqType {
  question: string;
  answer: string;
}

const faqs: FaqType[] = [
  {
    question: 'What is SwiftRamp Money?',
    answer: 'SwiftRamp Money is a comprehensive crypto payment solution for businesses that enables seamless conversion between fiat currencies and cryptocurrencies. It provides the infrastructure needed for businesses to integrate crypto payments into their existing systems.'
  },
  {
    question: 'Can individuals use SwiftRamp Money?',
    answer: 'SwiftRamp Money is primarily designed for businesses. Our solutions are tailored for companies looking to integrate crypto payment capabilities into their platforms or services.'
  },
  {
    question: 'What currencies and cryptocurrencies are supported?',
    answer: 'We support a wide range of fiat currencies and cryptocurrencies. This includes major fiat currencies like USD, EUR, GBP, INR and cryptocurrencies such as Bitcoin, Ethereum, USDT, and many more across 20+ blockchains.'
  },
  {
    question: 'What are the solutions available at SwiftRamp Money?',
    answer: 'SwiftRamp Money provides both SDK and API solutions which can be integrated based on the needs of the business. Both the solutions are customizable and works seamlessly. Apart from this, SwiftRamp Money also offers KYC and compliance offerings to meet the regulatory needs.'
  },
  {
    question: 'What platforms or environments does your SDK support?',
    answer: 'Our SDK supports a variety of platforms including web applications, mobile apps (iOS and Android), and server environments. The integration is designed to be flexible and adaptable to different tech stacks.'
  },
  {
    question: 'What are the advantages of using the web-hosted SDK?',
    answer: "The web-hosted SDK offers quick implementation, automatic updates, reduced maintenance burden, and consistent user experience across platforms. It's an ideal solution for businesses looking for a turnkey solution."
  },
  {
    question: 'Is there a sandbox environment for testing the API?',
    answer: 'Yes, we provide a comprehensive sandbox environment for testing all API functionalities before going live. This allows developers to thoroughly test integrations without affecting real transactions.'
  },
  {
    question: 'How do you ensure regulatory compliance?',
    answer: 'We maintain strict compliance with relevant regulations across jurisdictions. Our solution includes built-in KYC/AML processes, transaction monitoring, and regular audits to ensure all activities meet regulatory requirements.'
  }
];

interface FaqProps {
  answer: string;
  question: string;
  isExpanded: boolean;
  onToggle: () => void;
  index: number;
}

const Faq: FC<FaqProps> = (props) => {
  const { answer, question, isExpanded, onToggle  } = props;

  return (
    <Box>
      <Box
        onClick={onToggle}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
          py: 3,
          pl: 4,
          pr: 3,
          width: '100%'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography
            component="span"
            sx={{
              color: 'text.secondary',
              fontSize: '1rem',
              mr: 2,
              width: 24,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {isExpanded ? (
              <CloseIcon fontSize="small" />
            ) : (
              <AddIcon fontSize="small" />
            )}
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 500,
              color: isExpanded ? 'primary.main' : 'text.primary'
            }}
          >
            {question}
          </Typography>
        </Box>
      </Box>
      <Collapse in={isExpanded}>
        <Box sx={{ pl: 10, pr: 7, pb: 3 }}>
          <Typography
            color="text.secondary"
            variant="body2"
            sx={{
              lineHeight: 1.7
            }}
          >
            {answer}
          </Typography>
        </Box>
      </Collapse>
      <Divider />
    </Box>
  );
};

export const HomeFaqs: FC = () => {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(3);
  const [showAll, setShowAll] = useState<boolean>(false);

  const handleToggle = (index: number) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  const toggleShowAll = () => {
    setShowAll(!showAll);
    // If collapsing, make sure the expanded item is within the visible range
    if (showAll && expandedIndex !== null && expandedIndex > 2) {
      setExpandedIndex(null);
    }
  };

  // Display either the first 3 FAQs or all FAQs based on showAll state
  const displayedFaqs = showAll ? faqs : faqs.slice(0, 3);

  return (
    <Box
      component="section"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        width: '100%',
        backgroundColor: 'background.default',
        py: { xs: 8, md: 12 }
      }}
    >
      <Container maxWidth="md">
        <Typography
          variant="h3"
          align="center"
          sx={{
            mb: 6,
            fontWeight: 700
          }}
        >
          Frequently Asked Questions
        </Typography>

        <Box
          sx={{
            backgroundColor: '#f5f5f7', // Light gray background to match the image
            borderRadius: 2,
            overflow: 'hidden',
            mb: 4
          }}
        >
          {displayedFaqs.map((faq, index) => (
            <Faq
              key={index}
              index={index}
              isExpanded={expandedIndex === index}
              onToggle={() => handleToggle(index)}
              {...faq}
            />
          ))}
        </Box>

        <Box sx={{ textAlign: 'center', mt: 2 }}>
          <Button
            variant="text"
            color="primary"
            onClick={toggleShowAll}
            sx={{ fontWeight: 500 }}
          >
            {showAll ? 'View less...' : 'View more...'}
          </Button>
        </Box>
      </Container>
    </Box>
  );
};
